import { TZDate } from "@date-fns/tz"
import { format } from "date-fns"
import { V2ParamFormData, V2WindowFormData } from "@/store/packaging-v2"
import { ErrorFlagState } from "@/components/ui/input"
import { V2InputType } from "@/packaging-v2/lib/schema"
import { isValueWithinLimits } from "@/packaging-v2/lib/validators"
import { createUuid } from "@/lib/utils"

/**
 * Generate a unique ID for items, sections, etc.
 */
export function generateId(): string {
  return createUuid()
}

/**
 * Generate time windows based on shift start/end and frequency
 * @param frequencyMinutes - The frequency in minutes (60, 120, 180, 240)
 * @param shiftStart - Shift start time as Date
 * @param shiftEnd - Shift end time as Date
 * @returns Array of window objects with start/end times
 * 
 * Note: Uses today's date with the time from shiftStart/shiftEnd to ensure
 * correct date handling even if shift times have incorrect dates
 */
export function generateTimeWindows(
  frequencyMinutes: number,
  shiftStart: Date,
  shiftEnd: Date
): V2WindowFormData[] {
  const windows: V2WindowFormData[] = []
  const frequencyMs = frequencyMinutes * 60 * 1000

  // Use today's date with the time from shift times
  const today = new Date()
  const startHours = shiftStart.getHours()
  const startMinutes = shiftStart.getMinutes()
  const endHours = shiftEnd.getHours()
  const endMinutes = shiftEnd.getMinutes()

  // Create proper dates using today's date with shift times
  const windowStart = new Date(today)
  windowStart.setHours(startHours, startMinutes, 0, 0)

  const shiftEndDate = new Date(today)
  shiftEndDate.setHours(endHours, endMinutes, 0, 0)

  // Handle overnight shift (end time is before start time)
  if (shiftEndDate <= windowStart) {
    shiftEndDate.setDate(shiftEndDate.getDate() + 1)
  }

  let currentWindowStart = new Date(windowStart)

  while (currentWindowStart < shiftEndDate) {
    const windowEnd = new Date(currentWindowStart.getTime() + frequencyMs)

    // Don't create a window that extends past shift end
    if (windowEnd > shiftEndDate) {
      break
    }

    windows.push({
      id: generateId(),
      startTime: currentWindowStart.toISOString(),
      endTime: windowEnd.toISOString(),
      value: undefined,
      recordedAt: undefined,
      remark: undefined,
    })

    currentWindowStart = windowEnd
  }

  return windows
}

/**
 * Format time for display (e.g., "09:00")
 * Uses TZDate with UTC to ensure consistent time display regardless of browser locale
 */
export function formatTimeForDisplay(isoString: string): string {
  return format(new TZDate(isoString, "UTC"), "HH:mm")
}

/**
 * Format time window for display (e.g., "09:00 - 10:00")
 */
export function formatTimeWindowForDisplay(
  startTime: string,
  endTime: string
): string {
  return `${formatTimeForDisplay(startTime)} - ${formatTimeForDisplay(endTime)}`
}


/**
 * Check if a recording was made overtime (after window ended)
 */
export function isRecordingOvertime(
  recordedAt: string,
  windowEnd: string
): boolean {
  return new Date(recordedAt) > new Date(windowEnd)
}

/**
 * Check if a recording was made early (before window started)
 */
export function isRecordingEarly(
  recordedAt: string,
  windowStart: string
): boolean {
  return new Date(recordedAt) < new Date(windowStart)
}

/**
 * Get sequence label for an item (e.g., "01", "02")
 */
export function getItemSequenceLabel(sequenceOrder: number): string {
  return sequenceOrder.toString().padStart(2, "0")
}

/**
 * Parse shift times from store (handles overnight shifts)
 */
export function parseShiftTimes(
  startedAt: string,
  endedAt: string
): { shiftStart: Date; shiftEnd: Date } {
  const shiftStart = new Date(startedAt)
  let shiftEnd = new Date(endedAt)

  // Handle overnight shift (end time is before start time)
  if (shiftEnd <= shiftStart) {
    shiftEnd.setDate(shiftEnd.getDate() + 1)
  }

  return { shiftStart, shiftEnd }
}

/**
 * Deep clone an object
 */
export function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj))
}

/**
 * Get error flag state for a parameter based on validation
 * - For CHOICE: flag if value !== correctValue
 * - For VALUE_NUMBER: flag if value outside min/max limits
 * - Skip frequency params with windows (they are validated at window level)
 */
export function getParamErrorFlagState(param: V2ParamFormData): ErrorFlagState {
  if (!param.value) return ErrorFlagState.None

  // Skip frequency-based params - they are validated at window level via getWindowErrorFlagState
  if (param.frequencyMinutes && param.windows) {
    return ErrorFlagState.None
  }

  // Skip leakage-related params - they are validated separately via LeakageWidget
  const paramName = param.name.toLowerCase()
  if (paramName.includes('checked') || paramName.includes('leaked')) {
    return ErrorFlagState.None
  }

  // CHOICE: check against correctValue
  if (param.inputType === V2InputType.CHOICE && param.correctValue) {
    if (param.value !== param.correctValue) {
      return param.remark ? ErrorFlagState.Filled : ErrorFlagState.Unfilled
    }
  }

  // VALUE_NUMBER: check against limits
  if (param.inputType === V2InputType.VALUE_NUMBER) {
    if (param.minLimit !== undefined || param.maxLimit !== undefined) {
      const status = isValueWithinLimits(param.value, param.minLimit, param.maxLimit)
      if (status !== "valid") {
        return param.remark ? ErrorFlagState.Filled : ErrorFlagState.Unfilled
      }
    }
  }

  // LEAKAGE BREAKDOWN: require remark when there's leakage data
  // Only check parameters with "breakdown" in the name (not "Total Packets Leaked")
  if (param.name.toLowerCase().includes('breakdown') && param.value) {
    try {
      const data = JSON.parse(param.value)
      const hasEntries = data.entries && data.entries.length > 0
      const hasRemark = !!(data.remark || param.remark)

      if (hasEntries) {
        // Has leakage entries - require remark (check JSON data first, then param.remark)
        return hasRemark ? ErrorFlagState.Filled : ErrorFlagState.Unfilled
      }
    } catch {
      // Invalid JSON - treat as error
      return ErrorFlagState.Unfilled
    }
  }

  return ErrorFlagState.None
}

/**
 * Get error flag state for a window based on validation
 * - For CHOICE: flag if value !== correctValue
 * - For VALUE_NUMBER: flag if value outside min/max limits
 */
export function getWindowErrorFlagState(
  param: V2ParamFormData,
  window: V2WindowFormData
): ErrorFlagState {
  if (!window.value) return ErrorFlagState.None

  // CHOICE: check against correctValue
  if (param.inputType === V2InputType.CHOICE && param.correctValue) {
    if (window.value !== param.correctValue) {
      return window.remark ? ErrorFlagState.Filled : ErrorFlagState.Unfilled
    }
  }

  // VALUE_NUMBER: check against limits
  if (param.inputType === V2InputType.VALUE_NUMBER) {
    if (param.minLimit !== undefined || param.maxLimit !== undefined) {
      const status = isValueWithinLimits(window.value, param.minLimit, param.maxLimit)
      if (status !== "valid") {
        return window.remark ? ErrorFlagState.Filled : ErrorFlagState.Unfilled
      }
    }
  }

  return ErrorFlagState.None
}
